import React, { useState, useEffect, useRef } from "react";
import {
  Eye,
  Pencil,
  Bot,
  MoreHorizontal,
  Megaphone,
  LineChart,
} from "lucide-react";
import { Link, useFetcher } from "react-router-dom";
import { convertToZone, formatDateToShort } from "../../utils/formatters";
import { getProductDetail } from "../../services/store/productAxios";
import { Product, ProductType } from "../../interfaces/websites";
import ToggleSwitch from "../onboarding/ui/ToggleSwitch";
import { getOptimization, setOptimization } from "../../utils/indexedDBUtils";
import { dictionary } from "../../utils/dictionary";
import { useAuth } from "../../context/AuthContext";
import { getSectionFunnel, getSectionsGenerated } from "../../services/funnels";
import { database } from "../../lib/firebase";
import { ref, onValue } from "firebase/database";

interface FunnelsCardProps {
  id: string;
  idHtml: string;
  // name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  product: Product[];
  idSequence: number;
  handleDuplicate: () => void;
  handleEdit: () => void;
  handleOpenModal: () => void;
  handlePreview: () => void;
  handleAdsClick: () => void;
  sectionsGenerated: boolean | undefined;
}

export const FunnelsCard: React.FC<FunnelsCardProps> = ({
  id,
  idHtml,
  // name,
  description,
  isActive,
  createdAt,
  product,
  idSequence,
  handleDuplicate,
  handleEdit,
  handleOpenModal,
  handlePreview,
  handleAdsClick,
  sectionsGenerated,
}) => {
  const { language } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [messageVisible, setMessageVisible] = useState(true);
  const [image, setImage] = useState<string>("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [optimizationEnabled, setOptimizationEnabled] = useState(false);
  const [name, setName] = useState<string>("");
  const [loadingName, setLoadingName] = useState(true);

  const LOADING_MESSAGES = [
    dictionary["funnel-creation"][language],
    dictionary["researching-target-audience"][language],
    dictionary["uncovering-unique-insights"][language],
    dictionary["identifying-pain-points"][language],
    dictionary["writing-persuasive-copy"][language],
    dictionary["creating-high-quality-images"][language],
    dictionary["optimizing-results"][language],
    dictionary["refining-the-details"][language],
  ];

  useEffect(() => {
    if (sectionsGenerated) {
      setShowControls(true);
    }
  }, [sectionsGenerated]);

  const getSection = async () => {
    setLoadingName(true);
    getSectionFunnel(id, "product_details")
      .then((section) => {
        setName(section.content.product_name);
      })
      .catch((error) => {
        getSectionFunnel(id, "above_the_fold_principal")
          .then((section) => {
            setName(section.content.product_details_subsection.product_name);
          })
          .catch((error) => {
            console.error("Error fetching section:", error);
          });
      })
      .finally(() => {
        setLoadingName(false);
      });
  };

  useEffect(() => {
    getSection();
  }, []);

  useEffect(() => {
    const principalProduct = product.find(
      (p) => p.type === ProductType.PRINCIPAL
    );
    if (principalProduct) {
      getProductDetail(principalProduct.product_id)
        .then((product) => {
          setImage(product.base_image_url);
        })
        .catch((error) => {
          console.error("Error fetching product detail:", error);
        });
    }
  }, [id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!showControls) {
      const messageInterval = setInterval(() => {
        setMessageVisible(false);

        setTimeout(() => {
          setCurrentMessageIndex((prevIndex) => {
            const nextIndex = prevIndex + 1;
            if (nextIndex === LOADING_MESSAGES.length) {
              clearInterval(messageInterval);
              // setTimeout(() => {
              // setShowContr ols(true);
              // }, 1000);
              return prevIndex;
            }
            return nextIndex;
          });
          setMessageVisible(true);
        }, 300);
      }, 3000);

      return () => clearInterval(messageInterval);
    }
  }, [showControls]);

  useEffect(() => {
    const dataRef = ref(
      database,
      `${
        import.meta.env.VITE_PUBLIC_ENVIRONMENT
      }/websites/${id}/sections-generated`
    );
    const unsubscribe = onValue(dataRef, (snapshot) => {
      const value = snapshot.val();
      if ((value && Object.values(value).length > 0) || sectionsGenerated) {
        setShowControls(value?.done || false);
        if (sectionsGenerated) {
          setShowControls(true);
        }
      }
    });
    return () => unsubscribe();
  }, [id, sectionsGenerated]);

  return (
    <div
      id={idHtml}
      className={`flex flex-col justify-between rounded-[20px] shadow-sm border border-gray-200 overflow-hidden transition-colors duration-500 ${
        isProcessing ? "bg-gray-50" : "bg-white"
      }`}
    >
      <div className="p-6">
        <div className="flex items-start gap-3 border-b border-gray-100 pb-4">
          <img
            src={
              image ||
              "https://media.istockphoto.com/id/1147544807/vector/thumbnail-image-vector-graphic.jpg?s=612x612&w=0&k=20&c=rnCKVbdxqkjlcs3xH87-9gocETqpspHFXu5dIGB4wuM="
            }
            alt={name}
            className="w-12 h-12 object-cover rounded-lg"
          />
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div className="flex flex-col">
                {isProcessing ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 mb-1 self-start animate-pulse">
                    {dictionary["processing"][language]}
                  </span>
                ) : (
                  <span
                    className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      isActive
                        ? "bg-[#DCFCE7] text-[#15803D]"
                        : "bg-gray-100 text-gray-600"
                    } mb-1 self-start`}
                  >
                    {isActive && (
                      <span className="w-1.5 h-1.5 rounded-full bg-[#15803D] mr-1.5"></span>
                    )}
                    {isActive
                      ? dictionary["active"][language]
                      : dictionary["draft"][language]}
                  </span>
                )}
                {loadingName ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 mb-1 self-start animate-pulse">
                    {dictionary["processing"][language]}
                  </span>
                ) : (
                  <h3 className="text-[1rem] leading-[1.4rem] font-semibold text-gray-900">
                    {name}
                  </h3>
                )}
              </div>
              {showControls && (
                <div className="relative">
                  <button
                    ref={buttonRef}
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="p-1.5 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <MoreHorizontal size={18} className="text-gray-500" />
                  </button>
                  <div
                    ref={dropdownRef}
                    className={`
                      absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10
                      transition-all duration-200 ease-in-out origin-top-right
                      ${
                        showDropdown
                          ? "transform scale-100 opacity-100"
                          : "transform scale-95 opacity-0 pointer-events-none"
                      }
                    `}
                  >
                    <div className="px-4 py-2 text-left text-sm text-gray-500 mb-1 border-b border-gray-200">
                      {dictionary["created"][language]}:{" "}
                      {convertToZone(formatDateToShort(createdAt), "dd/MM/yy")}
                    </div>
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                      onClick={() => {
                        setShowDropdown(false);
                        handleDuplicate();
                      }}
                    >
                      {dictionary["duplicate"][language]}
                    </button>
                    <button
                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors"
                      onClick={() => {
                        setShowDropdown(false);
                        handleOpenModal();
                      }}
                    >
                      {dictionary["delete"][language]}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <div className="text-[.675rem] leading-[1.25rem] text-gray-500 mb-1">
            {dictionary["funnel"][language]} #{idSequence}
          </div>
          <p className="text-xl text-gray-900 font-semibold">{description}</p>
        </div>
      </div>

      {showControls ? (
        <div>
          <div className="px-6 pb-3 overflow-x-hidden">
            <div className="flex space-x-3">
              <button
                onClick={handlePreview}
                className="group relative flex items-center p-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full hover:pr-16 transition-all duration-[400ms] ease-in-out hover:bg-gray-200"
              >
                <Eye size={22} className="relative z-10" />
                <span className="absolute left-12 opacity-0 group-hover:opacity-100 transition-opacity duration-120 delay-100 text-sm font-medium whitespace-nowrap max-w-[60%] text-ellipsis">
                  {dictionary["view"][language]}
                </span>
              </button>
              <button
                onClick={handleEdit}
                className="group relative flex items-center p-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full hover:pr-16 transition-all duration-[400ms] ease-in-out hover:bg-gray-200"
              >
                <Pencil size={22} className="relative z-10" />
                <span className="absolute left-12 opacity-0 group-hover:opacity-100 transition-opacity duration-120 delay-100 text-sm font-medium whitespace-nowrap max-w-[60%] text-ellipsis">
                  {dictionary["edit"][language]}
                </span>
              </button>
              <button
                onClick={handleAdsClick}
                className="group relative flex items-center p-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full hover:pr-20 transition-all duration-[400ms] ease-in-out hover:bg-gray-200"
              >
                <Megaphone size={22} className="relative z-10" />
                <span className="absolute left-12 opacity-0 group-hover:opacity-100 transition-opacity duration-120 delay-100 text-sm font-medium truncate max-w-[100px] inline-block max-w-[60%] text-ellipsis">
                  {dictionary["ads"][language]}
                </span>
              </button>
              <Link
                to={`/analytics/${id}`}
                className="group relative flex items-center p-3.5 text-gray-600 hover:text-gray-800 bg-gray-100 rounded-full hover:pr-20 transition-all duration-[400ms] ease-in-out hover:bg-gray-200"
              >
                <LineChart size={22} className="relative z-10" />
                <span className="absolute left-12 opacity-0 group-hover:opacity-100 transition-opacity duration-120 delay-100 text-sm font-medium whitespace-nowrap text-ellipsis overflow-hidden max-w-[60%] text-ellipsis">
                  {dictionary["metrics"][language]}
                </span>
              </Link>
            </div>
          </div>
          {/* <div className="px-6 py-3 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <span className="text-sm font-medium text-gray-700">{dictionary["ai-auto-optimization"][language]}</span>
                <button className="ml-1.5 text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                  </svg>
                </button>
              </div>
              <ToggleSwitch
                enabled={optimizationEnabled}
                onChange={handleToggleOptimization}
              />
            </div>
          </div> */}
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div className="relative h-14 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="absolute inset-0 bg-gray-200 rounded-full transition-transform duration-1000 ease-in-out animate-pulse"
              style={{
                transform: `scaleX(${isProcessing ? "1" : "0"})`,
                transformOrigin: "left",
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <span
                className={`
                  text-sm text-gray-600 font-medium
                  transition-opacity duration-300 ease-in-out
                  ${
                    messageVisible
                      ? "opacity-100 transform translate-y-0"
                      : "opacity-0 transform -translate-y-2"
                  }
                `}
              >
                {LOADING_MESSAGES[currentMessageIndex]}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
